import { LitElement, css, html, nothing, svg } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';
import '@awesome.me/webawesome/dist/components/popup/popup.js';
import type WaPopup from '@awesome.me/webawesome/dist/components/popup/popup.js';

import '../ns-icon/ns-icon.js';
import { HoverTipController, hoverTipStyles } from '../helpers/hover-tip.js';
import { LiveAnnouncer } from '../helpers/live-announcer.js';
import { nsButtonStyles, nsSharedStyles } from '../ns-styles/index.js';
import { nsMotionKeyframes } from '../ns-styles/ns-motion-styles.js';
import { focusablesIn } from './resolve-target.js';
import type { BubblePlacement } from './tour-state.js';

interface Hole {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Room left around a target inside its hole, and the hole's corner radius
const HOLE_PADDING = 6;
const HOLE_RADIUS = 12;

// Relative to the scrim's own box, which is the viewport in the app but not inside a transformed
// ancestor (a gallery frame)
function holeFor(element: HTMLElement, origin: DOMRect): Hole {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left - origin.left - HOLE_PADDING,
    y: rect.top - origin.top - HOLE_PADDING,
    width: rect.width + HOLE_PADDING * 2,
    height: rect.height + HOLE_PADDING * 2,
  };
}

// A rounded rectangle as path data, drawn the opposite way round to the viewport so evenodd cuts it out
function holePath({ x, y, width, height }: Hole): string {
  const r = Math.min(HOLE_RADIUS, width / 2, height / 2);
  return `M${x + r},${y} h${width - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${height - 2 * r} a${r},${r} 0 0 1 -${r},${r} h-${width - 2 * r} a${r},${r} 0 0 1 -${r},-${r} v-${height - 2 * r} a${r},${r} 0 0 1 ${r},-${r} z`;
}

function sameHoles(a: Hole[], b: Hole[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (hole, i) => hole.x === b[i].x && hole.y === b[i].y && hole.width === b[i].width && hole.height === b[i].height,
    )
  );
}

// Draws the in-app guide: a scrim over the page with a hole round each target, and the step's bubble
// beside its anchor. The page is locked: the scrim swallows clicks except through the holes of
// interactive targets, and Tab only moves between those targets and the bubble's buttons. It knows
// nothing about tours; NSTutorialService decides what it shows.
@customElement('ns-tutorial-overlay')
export class NSTutorialOverlay extends LitElement {
  static styles = [
    nsSharedStyles,
    nsButtonStyles,
    nsMotionKeyframes,
    hoverTipStyles,
    css`
      :host {
        display: contents;
      }

      /* Above the side bar and flyouts (998-999), below the loader (9999). The bubble is in the top layer. */
      svg.scrim {
        position: fixed;
        inset: 0;
        width: 100vw;
        height: 100vh;
        z-index: 1000;
        pointer-events: none;
        animation: ns-tutorial-fade-in 200ms ease-out;
      }

      /* Between steps the dimming fades out and back in rather than switching off and on */
      svg.scrim.leaving {
        animation: ns-tutorial-scrim-out 200ms ease-in forwards;
      }

      @keyframes ns-tutorial-scrim-out {
        to {
          opacity: 0;
        }
      }

      @keyframes ns-tutorial-fade-in {
        from {
          opacity: 0;
        }
      }

      .shade {
        fill: color-mix(in srgb, var(--ns-theme-black) 50%, transparent);
      }

      .blocker {
        fill: transparent;
        pointer-events: fill;
      }

      .ring {
        fill: none;
        stroke: var(--wa-color-focus);
        stroke-width: 2;
      }

      wa-popup {
        --arrow-color: var(--wa-color-surface-default);
        --arrow-size: 8px;
      }

      wa-popup::part(popup) {
        z-index: 1001;
      }

      .bubble {
        box-sizing: border-box;
        max-width: min(22rem, calc(100vw - 2 * var(--wa-space-m)));
        padding: var(--wa-space-m);
        border-radius: var(--wa-border-radius-l);
        background: var(--wa-color-surface-default);
        color: var(--ns-theme-type);
        box-shadow: 0 8px 24px color-mix(in srgb, var(--ns-theme-black) 35%, transparent);
        font-family: var(--ns-theme-font-family);
        font-weight: 300;
        line-height: 1.4;
        animation: ns-tutorial-fade-in 200ms ease-out;
      }

      /* Focus rests on the bubble only to start a step there; it isn't a control */
      .bubble:focus {
        outline: none;
      }

      .bubble[hidden] {
        display: none;
      }

      /* A clicked action hides the bubble: it fades out rather than vanishing */
      .bubble.leaving {
        animation: ns-tutorial-fade-out 160ms ease-in forwards;
        pointer-events: none;
      }

      /* Only the opacity changes: the bubble stays where it is */
      @keyframes ns-tutorial-fade-out {
        to {
          opacity: 0;
        }
      }

      /* The text, with the skip controls in the top right corner */
      .header {
        display: flex;
        align-items: flex-start;
        gap: var(--wa-space-xs);
      }

      .text {
        flex: 1;
        margin: 0 0 var(--wa-space-s);
      }

      .skips {
        display: flex;
        align-items: center;
        margin-block-start: calc(-1 * var(--wa-space-xs));
        margin-inline-end: calc(-1 * var(--wa-space-xs));
      }

      /* Their labels open below them: to the left, End tour's would cover Skip section */
      .skips .tip {
        top: calc(100% + 4px);
        right: 0;
        transform: none;
      }

      .skips .tip::after {
        top: -4px;
        bottom: auto;
        left: 0;
        width: 100%;
        height: 4px;
      }

      .controls {
        display: flex;
        align-items: center;
        gap: var(--wa-space-2xs);
      }

      .position {
        flex: 1;
        font-size: var(--wa-font-size-xs);
        color: var(--ns-theme-grey-dark);
      }

      wa-icon {
        font-size: 1.1em;
      }
    `,
  ];

  // Every target, each drawn with a hole
  @property({ attribute: false }) targets: HTMLElement[] = [];

  // The targets clicks and Tab reach; the rest only show through
  @property({ attribute: false }) interactive: HTMLElement[] = [];

  @property({ attribute: false }) anchor?: HTMLElement;

  @property() text = '';

  @property() placement: BubblePlacement = 'bottom';

  @property({ type: Number }) index = 0;

  @property({ type: Number }) total = 0;

  @property({ type: Boolean, attribute: 'show-next' }) showNext = false;

  @property({ type: Boolean, attribute: 'last-step' }) lastStep = false;

  // A clicked action hides the bubble until it succeeds, and the dim and lock go with it
  @property({ type: Boolean, attribute: 'bubble-hidden' }) bubbleHidden = false;

  // The bubble fading out after bubbleHidden was set, until its animation ends
  @state() private __leaving = false;

  // The last step's holes while its scrim fades out, once the targets are gone
  @state() private __fadingHoles?: Hole[];

  @state() private __holes: Hole[] = [];

  @state() private __interactiveHoles: Hole[] = [];

  @query('wa-popup') private __popup?: WaPopup;

  @query('.bubble') private __bubble?: HTMLElement;

  @query('svg.scrim') private __scrim?: SVGSVGElement;

  @state() private __size = { width: 0, height: 0 };

  private __announcer = new LiveAnnouncer(this);

  private __tips = new HoverTipController(this);

  private __frame?: number;

  // A new step moves focus once its bubble has a box to take it
  private __focusPending = false;

  // A new object only when the anchor changes, so wa-popup doesn't restart on every render
  private __virtualAnchor?: { getBoundingClientRect: () => DOMRect };

  private __anchorFor?: HTMLElement;

  // Where the anchor last was, which the bubble keeps while it fades out
  private __anchorRect?: DOMRect;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this.__onKeyDown, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.__onKeyDown, true);
    this.__stopTracking();
  }

  protected willUpdate(changed: Map<string, unknown>) {
    // The dim and the lock go when the step ends or its bubble hides (a clicked action, until it
    // succeeds), fading out from where the holes last were, and come back with the next bubble
    if (changed.has('targets') || changed.has('bubbleHidden')) {
      const before = changed.has('targets') ? (changed.get('targets') as HTMLElement[] | undefined) : this.targets;
      const wasLocked =
        !!before?.length && !(changed.has('bubbleHidden') ? changed.get('bubbleHidden') : this.bubbleHidden);
      if (this.__locked) this.__fadingHoles = undefined;
      else if (wasLocked && this.__holes.length) this.__fadingHoles = this.__holes;
    }
    if (changed.has('bubbleHidden')) {
      const wasShowing = changed.get('bubbleHidden') === false && !!this.text && this.__holes.length > 0;
      this.__leaving = this.bubbleHidden && wasShowing;
    }
    if (changed.has('anchor') && this.anchor !== this.__anchorFor) {
      const anchor = this.anchor;
      this.__anchorFor = anchor;
      // While the bubble fades out it stays where it was, even if the card it points at changes size
      // (a failed Next adds its error line)
      this.__virtualAnchor = anchor
        ? {
            getBoundingClientRect: () => {
              if (!this.__leaving || !this.__anchorRect) this.__anchorRect = anchor.getBoundingClientRect();
              return this.__anchorRect;
            },
          }
        : undefined;
    }
  }

  protected updated(changed: Map<string, unknown>) {
    if (changed.has('targets') || changed.has('interactive')) {
      if (this.targets.length) this.__startTracking();
      else this.__stopTracking();
    }
    if ((changed.has('text') || changed.has('bubbleHidden')) && this.text && !this.bubbleHidden) {
      this.__announcer.announce(`Guide, step ${this.index} of ${this.total}: ${this.text}`);
      this.__focusPending = true;
    }
  }

  // Follows the targets as the page scrolls, animates and reflows; redraws only when a hole moved
  private __startTracking() {
    if (this.__frame !== undefined) return;
    const tick = () => {
      const origin = this.__scrim?.getBoundingClientRect() ?? new DOMRect(0, 0, window.innerWidth, window.innerHeight);
      const holes = this.targets.map((target) => holeFor(target, origin));
      const interactiveHoles = this.interactive.map((target) => holeFor(target, origin));
      const size = { width: origin.width, height: origin.height };
      if (size.width !== this.__size.width || size.height !== this.__size.height) this.__size = size;
      if (!sameHoles(holes, this.__holes) || !sameHoles(interactiveHoles, this.__interactiveHoles)) {
        this.__holes = holes;
        this.__interactiveHoles = interactiveHoles;
        if (!this.__leaving) this.__popup?.reposition();
      }
      if (this.__focusPending) this.__focusIntoStep();
      this.__frame = requestAnimationFrame(tick);
    };
    this.__frame = requestAnimationFrame(tick);
  }

  private __stopTracking() {
    if (this.__frame !== undefined) cancelAnimationFrame(this.__frame);
    this.__frame = undefined;
    this.__focusPending = false;
    this.__holes = [];
    this.__interactiveHoles = [];
  }

  // The bubble's own buttons, as Tab reaches them
  private get __bubbleFocusables(): HTMLElement[] {
    return this.__bubble && !this.bubbleHidden ? focusablesIn(this.__bubble) : [];
  }

  private get __allowedFocus(): HTMLElement[] {
    return [...this.interactive.flatMap((target) => focusablesIn(target)), ...this.__bubbleFocusables];
  }

  // Once the bubble is open, leaves focus where it is when it's already inside an interactive target
  // (onboarding focuses its card), and otherwise moves it to the bubble
  private __focusIntoStep() {
    const buttons = this.__bubbleFocusables;
    if (!buttons.length) return;
    this.__focusPending = false;
    const active = deepActiveElement();
    if (active && [...this.interactive, this.__bubble].some((element) => element && deepContains(element, active)))
      return;
    // Next when the step has it; otherwise the bubble itself, so no button's hover tip opens unasked
    const next = this.__bubble?.querySelector<HTMLElement>('wa-button.next');
    if (next) next.focus();
    else this.__bubble?.focus();
  }

  // The lock stops clicks, not scrolling: a wheel over the scrim scrolls whatever is under it
  private __onBlockedWheel = (event: WheelEvent) => {
    const blocker = event.currentTarget as SVGPathElement;
    blocker.style.pointerEvents = 'none';
    const under = deepElementFromPoint(event.clientX, event.clientY);
    blocker.style.pointerEvents = '';
    scrollableFrom(under)?.scrollBy({ left: event.deltaX, top: event.deltaY });
  };

  // Tab cycles only through the interactive targets and the bubble
  private __onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab' || !this.__locked) return;
    const allowed = this.__allowedFocus;
    if (!allowed.length) return;
    event.preventDefault();
    const active = deepActiveElement();
    const current = allowed.findIndex((element) => element === active);
    const step = event.shiftKey ? -1 : 1;
    const next = current === -1 ? (event.shiftKey ? allowed.length - 1 : 0) : current + step;
    allowed[(next + allowed.length) % allowed.length].focus();
  };

  private __emit(name: 'tutorial-next' | 'tutorial-skip-section' | 'tutorial-end') {
    this.dispatchEvent(new CustomEvent(name));
  }

  // The page is dimmed and locked while a step shows its bubble
  private get __locked(): boolean {
    return this.targets.length > 0 && !this.bubbleHidden;
  }

  private __renderScrim() {
    const { width, height } = this.__size;
    const viewport = `M0,0 H${width} V${height} H0 Z`;
    if (!this.__locked) {
      // Only shade while it fades: nothing is locked once the step has ended or its bubble hid
      if (!this.__fadingHoles) return nothing;
      return html`
        <svg
          class="scrim leaving"
          viewBox="0 0 ${width} ${height}"
          aria-hidden="true"
          @animationend=${() => (this.__fadingHoles = undefined)}
        >
          ${svg`<path class="shade" fill-rule="evenodd" d="${viewport} ${this.__fadingHoles.map(holePath).join(' ')}"></path>`}
        </svg>
      `;
    }
    return html`
      <svg class="scrim" viewBox="0 0 ${width} ${height}" aria-hidden="true">
        ${svg`
          <path class="shade" fill-rule="evenodd" d="${viewport} ${this.__holes.map(holePath).join(' ')}"></path>
          <path class="blocker" fill-rule="evenodd" d="${viewport} ${this.__interactiveHoles.map(holePath).join(' ')}" @wheel=${this.__onBlockedWheel}></path>
          ${this.__holes.map((hole) => svg`<path class="ring" d="${holePath(hole)}"></path>`)}
        `}
      </svg>
    `;
  }

  private __renderControl(label: string, icon: unknown, event: 'tutorial-skip-section' | 'tutorial-end') {
    return html`
      <div class="tip-anchor">
        <wa-button appearance="plain" class="icon-button" @click=${() => this.__emit(event)}>
          ${icon}
          <span class="visually-hidden">${label}</span>
        </wa-button>
        <span class="tip" aria-hidden="true">${label}</span>
      </div>
    `;
  }

  render() {
    const open = !!this.__virtualAnchor && !!this.text && this.__holes.length > 0;
    const showing = !this.bubbleHidden || this.__leaving;
    return html`
      ${this.__renderScrim()}
      <wa-popup
        .anchor=${this.__virtualAnchor}
        ?active=${open && showing}
        placement=${this.placement}
        flip
        flip-fallback-placements="top right left"
        shift
        shift-padding="16"
        distance="14"
        arrow
      >
        <div
          class="bubble ${this.__leaving ? 'leaving' : ''}"
          role="dialog"
          aria-label="Guide"
          tabindex="-1"
          ?hidden=${!open || !showing}
          @animationend=${(event: AnimationEvent) => {
            if (event.animationName === 'ns-tutorial-fade-out') this.__leaving = false;
          }}
        >
          <div class="header">
            <p class="text">${this.text}</p>
            <div class="skips">
              ${this.__renderControl(
                'Skip section',
                html`
                  <ns-icon icon="forward-fast" aria-hidden="true"></ns-icon>
                `,
                'tutorial-skip-section',
              )}
              ${this.__renderControl(
                'End tour',
                html`
                  <wa-icon name="xmark" library="system" variant="solid" aria-hidden="true"></wa-icon>
                `,
                'tutorial-end',
              )}
            </div>
          </div>
          <div class="controls">
            <span class="position">${this.index} of ${this.total}</span>
            ${this.showNext
              ? html`
                  <wa-button class="next" variant="brand" size="small" @click=${() => this.__emit('tutorial-next')}>
                    ${this.lastStep ? 'Done' : 'Next'}
                  </wa-button>
                `
              : nothing}
          </div>
        </div>
      </wa-popup>
      ${this.__announcer.render()}
    `;
  }
}

// Whether `node` is `container` or inside it, across shadow boundaries
function deepContains(container: Element, node: Node): boolean {
  for (let current: Node | null = node; current; ) {
    if (current === container) return true;
    current = current.parentNode ?? (current instanceof ShadowRoot ? current.host : null);
  }
  return false;
}

function deepElementFromPoint(x: number, y: number): Element | null {
  let element = document.elementFromPoint(x, y);
  while (element?.shadowRoot) {
    const inner = element.shadowRoot.elementFromPoint(x, y);
    if (!inner || inner === element) break;
    element = inner;
  }
  return element;
}

// The nearest ancestor that scrolls, across shadow boundaries
function scrollableFrom(start: Element | null): Element | null {
  for (let node: Node | null = start; node; ) {
    if (node instanceof Element) {
      const { overflowY, overflowX } = getComputedStyle(node);
      const scrolls = /auto|scroll/.test(overflowY + overflowX);
      if (scrolls && (node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth)) return node;
    }
    node = node.parentNode ?? (node instanceof ShadowRoot ? node.host : null);
  }
  return document.scrollingElement;
}

// The focused element, looking inside shadow roots
function deepActiveElement(): Element | null {
  let active = document.activeElement;
  while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
  return active;
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-tutorial-overlay': NSTutorialOverlay;
  }
}
